import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Stack,
  Divider,
  Box,
  Grid,
  FormGroup,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { NavLink, useParams } from 'react-router-dom';
import AvTimerIcon from '@mui/icons-material/AvTimer';
import HttpHelper from '../../utils/HttpHelper';
import { useJobContext } from '../../JobContext';
import useJobForm from '../../hooks/useJobForm';
import { handleDeleteClick } from '../../containers/ManageJobs/DeleteJob';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useNavigate } from 'react-router-dom';
import AssessmentCard from '../../components/Assessment/AssessmentCard';

interface Job {
  title: string;
  description: string;
  requirements: string;
  responsibilities: string;
  city: string;
  province: string;
  country: string;
  currentlyAvailable: boolean;
  datePosted: Date;
  salary: number;
  jobCategory: string;
  jobType: string;
  remote: boolean;
}

const JobDetailsHR: React.FC = () => {
  const navigate = useNavigate();

  const { jobId } = useParams<{ jobId: string }>();
  const [job, setJob] = useState<Job>();
  const [postedTimeAgo, setPostedTimeAgo] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [assessments, setAssessments] = useState<any[]>([]);


  const timeSincePosted = (date: Date) => {
    const now = new Date();
    const postedDate = new Date(date);
    const milliseconds = Math.abs(now.getTime() - postedDate.getTime());
    const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return 'Today';
    } else if (days === 1) {
      return '1 day ago';
    } else {
      return `${days} days ago`;
    }
  };
  const locationIcon = (
    <LocationOnIcon sx={{ '&&': { color: 'var(--job-status-green)' } }} />
  );
  const accessTimeIcon = (
    <AccessTimeIcon sx={{ '&&': { color: 'var(--job-status-green)' } }} />
  );
  const eventAvailableIcon = (
    <EventAvailableIcon sx={{ '&&': { color: 'var(--job-status-green)' } }} />
  );
  const avTimerIcon = (
    <AvTimerIcon sx={{ '&&': { color: 'var(--job-status-green)' } }} />
  );

  useEffect(() => {
    const updateJobDetails = async () => {
      const response = await HttpHelper.get(`jobs/${jobId}`);
      if (response && response.job) {
        const jobData = response.job;
        setJob({
          title: jobData.title,
          description: jobData.description,
          requirements: jobData.requirements,
          responsibilities: jobData.responsibilities,
          city: jobData.city,
          province: jobData.province,
          country: jobData.country,
          currentlyAvailable: jobData.currentlyAvailable,
          datePosted: jobData.datePosted,
          salary: jobData.salary,
          jobCategory: jobData.jobCategory,
          jobType: jobData.jobType,
          remote: jobData.remote
        });
        setPostedTimeAgo(timeSincePosted(new Date(jobData.datePosted)));
      }
    };
    updateJobDetails();
  }, [jobId]);


  const { jobTypeMapping } = useJobContext();

  const inverseJobType = Object.fromEntries(
    Object.entries(jobTypeMapping).map(([key, value]) => [value, key])
  );


  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleConfirmDelete = async () => {
    if (jobId) {
      await handleDeleteClick(Number(jobId));
      navigate('/manage-jobs');
    }
  };


  useEffect(() => {
    const updateAssessmentsDetails = async () => {
      try {
        const response = await HttpHelper.get(`jobs/${jobId}/assessments`);
        if (response && response.assessments) {
          setAssessments(response.assessments);
        }
      } catch (error) {
        console.error('Failed to fetch assessments', error);
      }
    };
    updateAssessmentsDetails();
  }, [jobId]);



  return (
    <Box sx={{
      width: '95%',
      marginLeft: 'auto',
      marginRight: 'auto',
      marginTop: '64px',
    }}>
      <Card>
        <CardContent className="detailsCardContent"
          sx={{
            marginTop: "24px",
            marginBottom: "24px",
            marginLeft: "48px",
            marginRight: "48px"
          }}>
          <Stack direction="row" spacing={1} mb={2}>
            <Typography
              alignSelf="end"
              align="left"
              variant="h4"
              component="h2"
              gutterBottom
              sx={{ width: '80%', fontWeight: 'bold' }}
            >
              {job ? job.title : ''}
            </Typography>
            <Box display="flex" width="100%">
              <Grid container spacing={4} justifyContent="flex-end">
                <Grid item>
                  <NavLink to={`/manage-jobs/edit/${jobId}`}>
                    <Button
                      variant="contained"
                      sx={{
                        '&&': {
                          backgroundColor: 'var(--status-under-review)',
                          '&:hover': { backgroundColor: 'var(--yellow-highlight)' },
                        },
                        marginTop: '20px',
                        padding: '10px 40px',
                        maxHeight: '45px',
                        maxWidth: '128px',
                        fontWeight: 'bold'
                      }}
                    >
                      Edit
                    </Button>
                  </NavLink>
                </Grid>
                <Grid item>
                  <Button
                    onClick={handleOpenDialog}
                    variant="contained"
                    sx={{
                      '&&': {
                        backgroundColor: 'var(--status-not-selected)',
                        '&:hover': { backgroundColor: 'var(--red-highlight)' },
                      },
                      marginTop: '20px',
                      padding: '10px 40px',
                      maxHeight: '45px',
                      maxWidth: '128px',
                      fontWeight: 'bold'
                    }}
                  >
                    Delete
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} mb={2}>
            <Chip icon={locationIcon} label={`${job ? job.city : ''}, ${job ? job.province : ''}, ${job ? job.country : ''}`} variant="outlined" />
            <Chip
              icon={accessTimeIcon}
              label={job ? inverseJobType[job.jobType] : ''}
              variant="outlined"
            />
            <Chip
              icon={eventAvailableIcon}
              label={job ? job.currentlyAvailable ? "Currently Available" : "Future Opening" : ''}
              variant="outlined"
            />
            <Chip icon={avTimerIcon} label={postedTimeAgo} variant="outlined" />
          </Stack>
          <Divider sx={{ my: 6 }} />
          <Typography align="left" variant="h6" paragraph>
            Job Description
          </Typography>
          <Typography align="left" variant="body1" paragraph component="div">
            {job && (
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(job.description || ''),
                }}
              />
            )}
          </Typography>
          <br />

          <Typography align="left" variant="h6" paragraph>
            Responsibilities
          </Typography>
          <Typography align="left" variant="body1" paragraph component="div">
            {job && (
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(job.responsibilities || ''),
                }}
              />
            )}
          </Typography>
          <br />

          <Typography align="left" variant="h6" paragraph>
            Requirements
          </Typography>
          <Typography align="left" variant="body1" paragraph component="div">
            {job && (
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(job.requirements || ''),
                }}
              />
            )}
          </Typography>

          <Typography align="left" variant="h6" paragraph>
            Assessments
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {assessments.map((assessment, index) => (
              <NavLink
                key={index}
                to={`/manage-assessments/view/${assessment.id}`}
                style={{ textDecoration: 'none'}}
              >
                <Chip label={assessment.title} variant="outlined" clickable />
              </NavLink>
            ))}

          </Stack>


        </CardContent>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete this job?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleConfirmDelete} autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    </Box>
  );
};

export default JobDetailsHR;
